import Dexie, { Table } from "dexie";
import "dexie-export-import";
import { config } from "../utils/config";

export interface Chat {
  id: string;
  description: string;
  totalTokens: number;
  createdAt: Date;
  pinned: boolean;
}

export interface Message {
  id: string;
  chatId: string;
  role: "system" | "assistant" | "user";
  content: string;
  createdAt: Date;
}

export interface Prompt {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
}

export interface Settings {
  id: "general";
  provider?: 'openai' | 'custom' | 'anthropic' | 'google';
  model?: string;
  // Provider-specific settings
  providerSettings?: {
    [key: string]: ProviderSettings;
  };
}

export interface ProviderSettings {
  apiKey?: string;
  apiType?: 'openai' | 'custom';
  apiAuth?: 'none' | 'bearer-token' | 'api-key';
  apiBase?: string;
  apiVersion?: string;
  model?: string;
  // Add any other provider-specific settings here
  [key: string]: any; // Allow other properties for extensibility
}

export class Database extends Dexie {
  chats!: Table<Chat>;
  messages!: Table<Message>;
  prompts!: Table<Prompt>;
  settings!: Table<Settings>;

  constructor() {
    super("chatpad");

    this.version(3).stores({
      chats: "id, createdAt",
      messages: "id, chatId, createdAt",
      prompts: "id, createdAt",
      settings: "id",
    }).upgrade(tx => {
      return tx.table("settings").toCollection().modify((setting: any) => {
        setting.provider = setting.openAiApiType || config.defaultProvider || 'openai';
        setting.apiKey = setting.openAiApiKey;
        setting.model = setting.openAiModel;
        setting.apiType = setting.openAiApiType;
        setting.apiAuth = setting.openAiApiAuth;
        setting.apiBase = setting.openAiApiBase;
        setting.apiVersion = setting.openAiApiVersion;
        delete setting.openAiApiKey;
        delete setting.openAiModel;
      });
    });

    // Add version 4 for migrating to provider-specific settings
    this.version(4).stores({
      chats: "id, createdAt",
      messages: "id, chatId, createdAt",
      prompts: "id, createdAt",
      settings: "id",
    }).upgrade(tx => {
      return tx.table("settings").toCollection().modify((setting: any) => {
        // Create providerSettings structure if it doesn't exist
        if (!setting.providerSettings) {
          setting.providerSettings = {};
        }
        
        // Get current provider
        const provider = setting.provider || config.defaultProvider;
        
        // If provider settings already exist for this provider, don't overwrite
        if (!setting.providerSettings[provider]) {
          // Create provider settings using existing flat settings
          setting.providerSettings[provider] = {
            apiKey: setting.apiKey,
            apiType: setting.apiType,
            apiAuth: setting.apiAuth,
            apiBase: setting.apiBase,
            apiVersion: setting.apiVersion,
            model: setting.model
          };
        }
        
        // Remove redundant top-level settings (but keep provider and model for compatibility)
        delete setting.apiKey;
        delete setting.apiType;
        delete setting.apiAuth;
        delete setting.apiBase;
        delete setting.apiVersion;
      });
    });

    this.version(2).stores({
      chats: "id, createdAt",
      messages: "id, chatId, createdAt",
      prompts: "id, createdAt",
      settings: "id, openAiApiKey, openAiModel, openAiApiType, openAiApiAuth, openAiApiBase, openAiApiVersion",
    });

    this.on("populate", async () => {
      const count = await db.settings.count();
      if (count === 0) {
        console.log("Populating settings with defaults...");
        
        // Create initial provider settings for default provider
        const initialProviderSettings: ProviderSettings = {
          apiType: config.defaultProvider === 'custom' ? 'custom' : 'openai',
          apiAuth: config.defaultAuth,
          apiKey: config.defaultKey || undefined,
          apiBase: config.defaultBase || undefined,
          apiVersion: config.defaultVersion || undefined,
          model: config.defaultModel
        };
        
        // Initialize settings with provider-specific structure
        db.settings.add({
          id: "general",
          provider: config.defaultProvider,
          model: config.defaultModel,
          providerSettings: {
            [config.defaultProvider]: initialProviderSettings
          }
        });
      }
    });
  }
}

export const db = new Database();
